import type {
  ArmyDeploymentTargetView,
  ArmyDeploymentTraceView,
  CommandDisbandTraceView,
  CommanderPlacementActionView,
  CommanderPlacementTargetView,
  CommanderPlacementTraceView,
  LaneViewId,
  MatchViewModel,
} from "../presentation/game-view-model";
import { formatResources } from "../presentation/resource-format";
import type { GameEvent } from "@fate-kingdom/engine";

interface DeveloperDiagnosticsProps {
  game: MatchViewModel;
  selectedLaneId: LaneViewId | undefined;
  selectedCommandCardInstanceId: string | undefined;
  selectedCommanderCardInstanceId: string | undefined;
  pendingCommanderActionType: CommanderPlacementActionView | undefined;
  commanderPreview: CommanderPlacementTargetView | undefined;
  lastCommandDisbandTrace: CommandDisbandTraceView | undefined;
  lastCommanderPlacementTrace: CommanderPlacementTraceView | undefined;
  selectedArmyCardInstanceId: string | undefined;
  armyPreview: ArmyDeploymentTargetView | undefined;
  lastArmyDeploymentTrace: ArmyDeploymentTraceView | undefined;
  lastEvents?: readonly GameEvent[];
}

function changed(before: string | number | boolean, after: string | number | boolean): string {
  return `${before} -> ${after}`;
}

function armyCommanderDiagnostics(target: ArmyDeploymentTargetView): string {
  return `Presence ${target.commanderPresent ? "present" : "absent"} / Readiness ${target.commanderReady ? "ready" : "not ready"} / Restriction deployedThisTurn ${target.commanderDeployedThisTurn === undefined ? "not applicable" : String(target.commanderDeployedThisTurn)} / mayAttackOnDeploymentTurn ${target.commanderMayAttackOnDeploymentTurn === undefined ? "not applicable" : String(target.commanderMayAttackOnDeploymentTurn)}`;
}

export function DeveloperDiagnostics({
  game,
  selectedLaneId,
  selectedCommandCardInstanceId,
  selectedCommanderCardInstanceId,
  pendingCommanderActionType,
  commanderPreview,
  lastCommandDisbandTrace,
  lastCommanderPlacementTrace,
  selectedArmyCardInstanceId,
  armyPreview,
  lastArmyDeploymentTrace,
  lastEvents = [],
}: DeveloperDiagnosticsProps) {
  const selectedCommander = game.activeHand.find(
    (card) => card.instanceId === selectedCommanderCardInstanceId,
  );
  const selectedArmy = game.activeHand.find(
    (card) => card.instanceId === selectedArmyCardInstanceId,
  );
  const cleanupHand = lastEvents.find(
    (event): event is Extract<GameEvent, { readonly type: "cleanup-hand-discarded" }> =>
      event.type === "cleanup-hand-discarded",
  );
  const cleanupPool = lastEvents.find(
    (event): event is Extract<GameEvent, { readonly type: "kingdom-pool-cleared" }> =>
      event.type === "kingdom-pool-cleared",
  );
  const cleanupUsage = lastEvents.find(
    (event): event is Extract<GameEvent, { readonly type: "turn-action-usage-reset" }> =>
      event.type === "turn-action-usage-reset",
  );
  const cleanupModifiers = lastEvents.find(
    (event): event is Extract<GameEvent, { readonly type: "temporary-combat-modifiers-cleared" }> =>
      event.type === "temporary-combat-modifiers-cleared",
  );
  return (
    <details className="developer-diagnostics">
      <summary>Developer diagnostics</summary>
      <dl>
        <div>
          <dt>Phase</dt>
          <dd>{game.phase}</dd>
        </div>
        <div>
          <dt>Seed</dt>
          <dd>{game.seed}</dd>
        </div>
        <div>
          <dt>Catalog</dt>
          <dd>{game.gameplayCatalogHash ?? "legacy combat fixture"}</dd>
        </div>
        <div>
          <dt>Schema/replay</dt>
          <dd>
            V{game.schemaVersion} / Replay V{game.replayVersion}
          </dd>
        </div>
        <div>
          <dt>RNG</dt>
          <dd>
            {game.randomDraws} draws · {game.randomValue}
          </dd>
        </div>
        <div>
          <dt>Starting player</dt>
          <dd>{game.startingPlayerId ?? "legacy fixture"}</dd>
        </div>
        <div>
          <dt>Opening attempts</dt>
          <dd>
            {game.openingHandAttempts
              ? `P1 ${game.openingHandAttempts["player-1"]} · P2 ${game.openingHandAttempts["player-2"]}`
              : "not applicable"}
          </dd>
        </div>
        <div>
          <dt>First Draw</dt>
          <dd>
            {game.firstTurnDrawStatus
              ? `P1 ${game.firstTurnDrawStatus["player-1"]} · P2 ${game.firstTurnDrawStatus["player-2"]}`
              : "not applicable"}
          </dd>
        </div>
        <div>
          <dt>Active zones</dt>
          <dd>
            Deck {game.activePlayerZones.deck} · Hand {game.activePlayerZones.hand} · Discard{" "}
            {game.activePlayerZones.discard} · Exhaust {game.activePlayerZones.exhaust}
          </dd>
        </div>
        <div>
          <dt>Land Action</dt>
          <dd>{game.landActionsUsed}/1 used</dd>
        </div>
        <div>
          <dt>Deployment Action</dt>
          <dd>{game.deploymentActionsUsed}/1 used</dd>
        </div>
        <div>
          <dt>Supply</dt>
          <dd>
            {game.supply.status} / {game.supply.playerId ?? "no player"} / Turn{" "}
            {game.supply.turnNumber ?? "-"}
          </dd>
        </div>
        <div>
          <dt>Supply actions</dt>
          <dd>{game.supply.legalDiscardActionCount} legal discards</dd>
        </div>
        <div>
          <dt>Supply lifecycle</dt>
          <dd>{game.supplyLifecycleStatus}</dd>
        </div>
        <div>
          <dt>Pool transfer</dt>
          <dd>{formatResources(game.supply.kingdomPoolContribution)}</dd>
        </div>
        <div>
          <dt>Kingdom Pool</dt>
          <dd>{formatResources(game.supply.kingdomPool)}</dd>
        </div>
        {cleanupHand !== undefined && cleanupPool !== undefined && cleanupUsage !== undefined ? (
          <>
            <div>
              <dt>Cleanup event order</dt>
              <dd>{lastEvents.map((event) => event.type).join(" -> ")}</dd>
            </div>
            <div>
              <dt>Cleanup hand</dt>
              <dd>
                {cleanupHand.handCountBefore} -&gt; {cleanupHand.handCountAfter} / Discard{" "}
                {cleanupHand.discardCountBefore} -&gt; {cleanupHand.discardCountAfter} / [{" "}
                {cleanupHand.cardInstanceIds.join(", ")} ]
              </dd>
            </div>
            <div>
              <dt>Cleanup Pool</dt>
              <dd>
                {formatResources(cleanupPool.clearedResources)} -&gt;{" "}
                {formatResources(cleanupPool.resultingPool)}
              </dd>
            </div>
            <div>
              <dt>Cleanup usage</dt>
              <dd>
                Land {cleanupUsage.previousActionUsage.landActions} / Deploy{" "}
                {cleanupUsage.previousActionUsage.deploymentActions} / Governance{" "}
                {cleanupUsage.previousActionUsage.governanceActions} / Events{" "}
                {cleanupUsage.previousActionUsage.eventsPlayed} / Event lanes [
                {cleanupUsage.previousActionUsage.eventAuthorizationLanesUsed.join(", ")}] /
                Attacked [{cleanupUsage.previousActionUsage.attacksMadeFromLanes.join(", ")}]
              </dd>
            </div>
            <div>
              <dt>Cleanup modifiers</dt>
              <dd>
                {cleanupModifiers === undefined
                  ? "none"
                  : cleanupModifiers.removals
                      .map(
                        (removal) =>
                          `${removal.laneId}:${removal.affectedPlayerId} [${removal.modifierIds.join(", ")}]`,
                      )
                      .join("; ")}
              </dd>
            </div>
          </>
        ) : null}
        {game.lanes.map((lane) => {
          const land = lane.sides[game.activePlayerId];
          return (
            <div key={lane.laneId}>
              <dt>{lane.label} Land</dt>
              <dd>
                {land.hasLand
                  ? `${land.landTypeId ?? "legacy type"} / ${land.landDevelopmentId} / Stage ${land.landStage} / ${land.landControl}/${land.landMaxControl} / [${land.landCardInstanceIds.join(", ")}]`
                  : "empty"}
              </dd>
            </div>
          );
        })}
        {game.supply.lanes.map((lane) => (
          <div key={`supply-${lane.laneId}`}>
            <dt>{lane.label} Supply</dt>
            <dd>
              Type {lane.landTypeId ?? "none"} / Stage {lane.landStage ?? "-"} / Produces{" "}
              {formatResources(lane.production)} / Upkeep {formatResources(lane.upkeep)} / Shortfall{" "}
              {formatResources(lane.shortfall)} / Remainder {formatResources(lane.remainder)} /{" "}
              {lane.sustainable ? "sustainable" : "UNSUSTAINABLE"}
              {lane.armies.length === 0
                ? " / no Armies"
                : ` / Armies [${lane.armies
                    .map(
                      (army) =>
                        `${army.instanceId} ${army.definitionId} upkeep=${formatResources(army.upkeep)}${army.legalDiscard ? " legal-discard" : ""}`,
                    )
                    .join("; ")}]`}
            </dd>
          </div>
        ))}
        <div>
          <dt>Command actions</dt>
          <dd>
            {game.command.legalDisbandActionCount} disbands /{" "}
            {game.command.legalCommanderActionCount} Commander placements /{" "}
            {game.command.legalArmyDeploymentActionCount} Army deployments
          </dd>
        </div>
        <div>
          <dt>Legal Army deployment actions</dt>
          <dd>
            {game.command.legalArmyDeploymentActions.length === 0
              ? "none"
              : game.command.legalArmyDeploymentActions
                  .map(
                    (action) =>
                      `deploy-army:${action.playerId}:${action.armyCardInstanceId}:${action.laneId}`,
                  )
                  .join("; ")}
          </dd>
        </div>
        <div>
          <dt>Command selection</dt>
          <dd>
            Lane {selectedLaneId ?? "none"} / Card {selectedCommandCardInstanceId ?? "none"}
          </dd>
        </div>
        <div>
          <dt>Commander selection</dt>
          <dd>
            Card {selectedCommanderCardInstanceId ?? "none"} / Action{" "}
            {pendingCommanderActionType ?? "none"} / Lane {selectedLaneId ?? "none"}
          </dd>
        </div>
        <div>
          <dt>Army selection</dt>
          <dd>
            Card {selectedArmyCardInstanceId ?? "none"} / Lane {selectedLaneId ?? "none"}
          </dd>
        </div>
        {selectedCommander?.commander ? (
          <>
            <div>
              <dt>Selected definition</dt>
              <dd>
                {selectedCommander.definitionId} / Cost{" "}
                {formatResources(selectedCommander.commander.cost)} / Attack{" "}
                {selectedCommander.commander.attack} / Guard {selectedCommander.commander.guard} /
                Capacity {selectedCommander.commander.armyCapacity} / Mandates deferred ({" "}
                {selectedCommander.commander.mandateCount})
              </dd>
            </div>
            <div>
              <dt>Commander targets</dt>
              <dd>
                {selectedCommander.commander.targets
                  .map((target) =>
                    target.legal
                      ? `${target.laneId}:${target.actionType}:legal`
                      : `${target.laneId}:${target.actionType}:${target.rejectionCode ?? "rejected"}:${target.rejectionMessage ?? "no message"}`,
                  )
                  .join("; ")}
              </dd>
            </div>
          </>
        ) : null}
        {selectedArmy?.army ? (
          <>
            <div>
              <dt>Selected Army definition</dt>
              <dd>
                {selectedArmy.definitionId} / Cost {formatResources(selectedArmy.army.cost)} / Size{" "}
                {selectedArmy.army.armySize} / Attack {selectedArmy.army.attack} / Guard{" "}
                {selectedArmy.army.guard} / Upkeep {formatResources(selectedArmy.army.upkeep)}
              </dd>
            </div>
            <div>
              <dt>Army targets</dt>
              <dd>
                {selectedArmy.army.targets
                  .map((target) => {
                    const validation = target.legal
                      ? `legal:supply-${target.projectedNextSupplySustainable ? "sustainable" : "shortfall"}`
                      : `${target.rejectionCode ?? "rejected"}:${target.rejectionMessage ?? "no message"}`;
                    return `${target.laneId}:deploy-army:${validation} / Commander ${armyCommanderDiagnostics(target)}`;
                  })
                  .join("; ")}
              </dd>
            </div>
          </>
        ) : null}
        {armyPreview ? (
          <>
            <div>
              <dt>Army deployment preview</dt>
              <dd>
                {armyPreview.laneId} / legal {String(armyPreview.legal)} / Zone{" "}
                {armyPreview.incomingZoneBefore} -&gt;{" "}
                {armyPreview.incomingZoneAfter ?? "not previewed"}
              </dd>
            </div>
            <div>
              <dt>Army preview Pool</dt>
              <dd>
                {formatResources(armyPreview.kingdomPoolBefore)} -&gt;{" "}
                {formatResources(armyPreview.kingdomPoolAfter ?? armyPreview.kingdomPoolBefore)}
              </dd>
            </div>
            <div>
              <dt>Army preview garrison</dt>
              <dd>
                Count {armyPreview.armyCountBefore} -&gt; {armyPreview.armyCountAfter ?? "rejected"}{" "}
                / maximum {armyPreview.maximumArmyCount}/ Size {armyPreview.totalArmySizeBefore}{" "}
                -&gt; {armyPreview.totalArmySizeAfter ?? "rejected"} / Capacity{" "}
                {armyPreview.commanderCapacity ?? "none"}
              </dd>
            </div>
            <div>
              <dt>Army preview Commander</dt>
              <dd>{armyCommanderDiagnostics(armyPreview)}</dd>
            </div>
            <div>
              <dt>Army preview combat</dt>
              <dd>
                Attack {armyPreview.totalAttackBefore} -&gt;{" "}
                {armyPreview.totalAttackAfter ?? armyPreview.totalAttackBefore} / Army Attack{" "}
                {armyPreview.armyAttackContributionBefore} -&gt;{" "}
                {armyPreview.armyAttackContributionAfter ??
                  armyPreview.armyAttackContributionBefore}
                / Guard {armyPreview.totalGuardBefore} -&gt;{" "}
                {armyPreview.totalGuardAfter ?? armyPreview.totalGuardBefore}
              </dd>
            </div>
            <div>
              <dt>Army preview next Supply</dt>
              <dd>
                Production {formatResources(armyPreview.laneProduction)} / Upkeep{" "}
                {formatResources(armyPreview.totalUpkeepAfter ?? armyPreview.totalUpkeepBefore)} /
                Shortfall{" "}
                {formatResources(armyPreview.shortfallAfter ?? armyPreview.totalUpkeepBefore)}/
                Sustainable {String(armyPreview.projectedNextSupplySustainable)}
              </dd>
            </div>
            <div>
              <dt>Army preview stability</dt>
              <dd>
                Deployment {armyPreview.deploymentActionsUsedBefore} -&gt;{" "}
                {armyPreview.deploymentActionsUsedAfter ?? "not previewed"} / Supply{" "}
                {armyPreview.supplyLifecycleBefore} -&gt;{" "}
                {armyPreview.supplyLifecycleAfter ?? armyPreview.supplyLifecycleBefore} / RNG{" "}
                {armyPreview.randomDrawsBefore}:{armyPreview.randomValueBefore} -&gt;{" "}
                {armyPreview.randomDrawsAfter ?? armyPreview.randomDrawsBefore}:{" "}
                {armyPreview.randomValueAfter ?? armyPreview.randomValueBefore} / Hash{" "}
                {armyPreview.stateHashBefore} -&gt; {armyPreview.stateHashAfter ?? "not previewed"}
              </dd>
            </div>
          </>
        ) : null}
        {commanderPreview ? (
          <>
            <div>
              <dt>Placement preview</dt>
              <dd>
                {commanderPreview.actionType} / {commanderPreview.laneId} / Current{" "}
                {commanderPreview.currentCommander?.instanceId ?? "empty"} / Old destination{" "}
                {commanderPreview.outgoingZoneAfter ?? "not applicable"}
              </dd>
            </div>
            <div>
              <dt>Preview Pool</dt>
              <dd>
                {formatResources(commanderPreview.kingdomPoolBefore)} -&gt;{" "}
                {formatResources(
                  commanderPreview.kingdomPoolAfter ?? commanderPreview.kingdomPoolBefore,
                )}
              </dd>
            </div>
            <div>
              <dt>Preview garrison</dt>
              <dd>
                Armies {commanderPreview.armyCount} / Size {commanderPreview.totalArmySize} / Attack{" "}
                {commanderPreview.totalAttackBefore} -&gt;{" "}
                {commanderPreview.totalAttackAfter ?? commanderPreview.totalAttackBefore} / Guard{" "}
                {commanderPreview.totalGuardBefore} -&gt;{" "}
                {commanderPreview.totalGuardAfter ?? commanderPreview.totalGuardBefore}
              </dd>
            </div>
            <div>
              <dt>Preview Army Attack</dt>
              <dd>
                {commanderPreview.armyAttackContributionBefore} -&gt;{" "}
                {commanderPreview.armyAttackContributionAfter ??
                  commanderPreview.armyAttackContributionBefore}
              </dd>
            </div>
            <div>
              <dt>Preview Event</dt>
              <dd>
                Limit {commanderPreview.eventLimitBefore} -&gt;{" "}
                {commanderPreview.eventLimitAfter ?? commanderPreview.eventLimitBefore} / Lane used{" "}
                {String(commanderPreview.eventAuthorizationAlreadyUsed)}
              </dd>
            </div>
            <div>
              <dt>Preview restriction</dt>
              <dd>
                deployedThisTurn {String(commanderPreview.deployedThisTurnAfter)} /
                mayAttackOnDeploymentTurn {String(commanderPreview.mayAttackOnDeploymentTurnAfter)}{" "}
                / eligible {String(commanderPreview.attackEligibleAfter)} /
                {commanderPreview.attackValidationCodeAfter ?? "valid"}
              </dd>
            </div>
            <div>
              <dt>Preview stability</dt>
              <dd>
                Supply {commanderPreview.supplyLifecycleBefore} -&gt;{" "}
                {commanderPreview.supplyLifecycleAfter ?? commanderPreview.supplyLifecycleBefore} /
                RNG {commanderPreview.randomDrawsBefore}:{commanderPreview.randomValueBefore} -&gt;{" "}
                {commanderPreview.randomDrawsAfter ?? commanderPreview.randomDrawsBefore}:{" "}
                {commanderPreview.randomValueAfter ?? commanderPreview.randomValueBefore} / Hash{" "}
                {commanderPreview.stateHashBefore} -&gt;{" "}
                {commanderPreview.stateHashAfter ?? "not previewed"}
              </dd>
            </div>
          </>
        ) : null}
        {game.command.lanes.map((lane) => (
          <div key={`command-${lane.laneId}`}>
            <dt>{lane.label} Command</dt>
            <dd>
              {lane.commanderPresent ? "Commander" : "Garrison"} / Armies {lane.armyCount} / Size{" "}
              {lane.totalArmySize} / Attack {lane.totalAttack} / Guard {lane.totalGuard} / Choices [
              {lane.legalChoices
                .map(
                  (choice) =>
                    `${choice.type}:${choice.instanceId}:${choice.definitionId}:${choice.name}`,
                )
                .join("; ")}
              ]
            </dd>
          </div>
        ))}
        {lastCommandDisbandTrace ? (
          <>
            <div>
              <dt>Last disband</dt>
              <dd>
                {lastCommandDisbandTrace.actionType} / {lastCommandDisbandTrace.laneId} /{" "}
                {lastCommandDisbandTrace.cardType} / {lastCommandDisbandTrace.cardInstanceId} /{" "}
                {lastCommandDisbandTrace.cardDefinitionId}
              </dd>
            </div>
            <div>
              <dt>Zone</dt>
              <dd>
                {changed(lastCommandDisbandTrace.before.zone, lastCommandDisbandTrace.after.zone)}
              </dd>
            </div>
            <div>
              <dt>Army count</dt>
              <dd>
                {changed(
                  lastCommandDisbandTrace.before.armyCount,
                  lastCommandDisbandTrace.after.armyCount,
                )}
              </dd>
            </div>
            <div>
              <dt>Army Size</dt>
              <dd>
                {changed(
                  lastCommandDisbandTrace.before.totalArmySize,
                  lastCommandDisbandTrace.after.totalArmySize,
                )}
              </dd>
            </div>
            <div>
              <dt>Attack</dt>
              <dd>
                {changed(
                  lastCommandDisbandTrace.before.totalAttack,
                  lastCommandDisbandTrace.after.totalAttack,
                )}
              </dd>
            </div>
            <div>
              <dt>Guard</dt>
              <dd>
                {changed(
                  lastCommandDisbandTrace.before.totalGuard,
                  lastCommandDisbandTrace.after.totalGuard,
                )}
              </dd>
            </div>
            <div>
              <dt>Event Limit</dt>
              <dd>
                {changed(
                  lastCommandDisbandTrace.before.eventLimit,
                  lastCommandDisbandTrace.after.eventLimit,
                )}
              </dd>
            </div>
            <div>
              <dt>Deployment Action</dt>
              <dd>
                {changed(
                  lastCommandDisbandTrace.before.deploymentActionsUsed,
                  lastCommandDisbandTrace.after.deploymentActionsUsed,
                )}
              </dd>
            </div>
            <div>
              <dt>Pool</dt>
              <dd>
                {formatResources(lastCommandDisbandTrace.before.kingdomPool)} -&gt;{" "}
                {formatResources(lastCommandDisbandTrace.after.kingdomPool)}
              </dd>
            </div>
            <div>
              <dt>Supply marker</dt>
              <dd>
                {changed(
                  lastCommandDisbandTrace.before.supplyLifecycleStatus,
                  lastCommandDisbandTrace.after.supplyLifecycleStatus,
                )}
              </dd>
            </div>
            <div>
              <dt>RNG draws</dt>
              <dd>
                {changed(
                  lastCommandDisbandTrace.before.randomDraws,
                  lastCommandDisbandTrace.after.randomDraws,
                )}
              </dd>
            </div>
            <div>
              <dt>State hashes</dt>
              <dd>
                {lastCommandDisbandTrace.before.stateHash} -&gt;{" "}
                {lastCommandDisbandTrace.after.stateHash}
              </dd>
            </div>
          </>
        ) : null}
        {lastCommanderPlacementTrace ? (
          <>
            <div>
              <dt>Last placement</dt>
              <dd>
                {lastCommanderPlacementTrace.actionType} / {lastCommanderPlacementTrace.laneId} /{" "}
                {lastCommanderPlacementTrace.commanderCardInstanceId} /{" "}
                {lastCommanderPlacementTrace.commanderDefinitionId}
              </dd>
            </div>
            <div>
              <dt>Outgoing Commander</dt>
              <dd>
                {lastCommanderPlacementTrace.outgoingCommanderCardInstanceId ?? "none"} /{" "}
                {lastCommanderPlacementTrace.outgoingCommanderDefinitionId ?? "none"} /{" "}
                {lastCommanderPlacementTrace.outgoingCommanderDestination ?? "not applicable"}
              </dd>
            </div>
            <div>
              <dt>Placement zones</dt>
              <dd>
                Incoming{" "}
                {changed(
                  lastCommanderPlacementTrace.before.incomingZone,
                  lastCommanderPlacementTrace.after.incomingZone,
                )}
                {lastCommanderPlacementTrace.before.outgoingZone === undefined ||
                lastCommanderPlacementTrace.after.outgoingZone === undefined
                  ? ""
                  : ` / Outgoing ${changed(lastCommanderPlacementTrace.before.outgoingZone, lastCommanderPlacementTrace.after.outgoingZone)}`}
              </dd>
            </div>
            <div>
              <dt>Cost / Pool</dt>
              <dd>
                Paid {formatResources(lastCommanderPlacementTrace.costPaid)} /{" "}
                {formatResources(lastCommanderPlacementTrace.before.kingdomPool)} -&gt;{" "}
                {formatResources(lastCommanderPlacementTrace.after.kingdomPool)}
              </dd>
            </div>
            <div>
              <dt>Placement totals</dt>
              <dd>
                Armies{" "}
                {changed(
                  lastCommanderPlacementTrace.before.armyCount,
                  lastCommanderPlacementTrace.after.armyCount,
                )}
                / Size{" "}
                {changed(
                  lastCommanderPlacementTrace.before.totalArmySize,
                  lastCommanderPlacementTrace.after.totalArmySize,
                )}
                / Attack{" "}
                {changed(
                  lastCommanderPlacementTrace.before.totalAttack,
                  lastCommanderPlacementTrace.after.totalAttack,
                )}
                / Guard{" "}
                {changed(
                  lastCommanderPlacementTrace.before.totalGuard,
                  lastCommanderPlacementTrace.after.totalGuard,
                )}
              </dd>
            </div>
            <div>
              <dt>Army Attack contribution</dt>
              <dd>
                {changed(
                  lastCommanderPlacementTrace.before.armyAttackContribution,
                  lastCommanderPlacementTrace.after.armyAttackContribution,
                )}
              </dd>
            </div>
            <div>
              <dt>Placement Event</dt>
              <dd>
                Limit{" "}
                {changed(
                  lastCommanderPlacementTrace.before.eventLimit,
                  lastCommanderPlacementTrace.after.eventLimit,
                )}
                / Used before [
                {lastCommanderPlacementTrace.before.eventAuthorizationLanesUsed.join(", ")}] / after
                [{lastCommanderPlacementTrace.after.eventAuthorizationLanesUsed.join(", ")}] /
                selected lane already used{" "}
                {String(lastCommanderPlacementTrace.authorizationLaneAlreadyUsed)}
              </dd>
            </div>
            <div>
              <dt>Placement restriction</dt>
              <dd>
                deployedThisTurn {String(lastCommanderPlacementTrace.after.deployedThisTurn)} /
                mayAttackOnDeploymentTurn{" "}
                {String(lastCommanderPlacementTrace.after.mayAttackOnDeploymentTurn)} / eligible{" "}
                {String(lastCommanderPlacementTrace.after.attackEligible)} /{" "}
                {lastCommanderPlacementTrace.after.attackValidationCode ?? "valid"}
              </dd>
            </div>
            <div>
              <dt>Placement stability</dt>
              <dd>
                Deployment{" "}
                {changed(
                  lastCommanderPlacementTrace.before.deploymentActionsUsed,
                  lastCommanderPlacementTrace.after.deploymentActionsUsed,
                )}
                / Supply{" "}
                {changed(
                  lastCommanderPlacementTrace.before.supplyLifecycleStatus,
                  lastCommanderPlacementTrace.after.supplyLifecycleStatus,
                )}
                / RNG draws{" "}
                {changed(
                  lastCommanderPlacementTrace.before.randomDraws,
                  lastCommanderPlacementTrace.after.randomDraws,
                )}
                / State {lastCommanderPlacementTrace.before.stateHash} -&gt;{" "}
                {lastCommanderPlacementTrace.after.stateHash}
              </dd>
            </div>
          </>
        ) : null}
        {lastArmyDeploymentTrace ? (
          <>
            <div>
              <dt>Last Army deployment</dt>
              <dd>
                {lastArmyDeploymentTrace.actionType} / {lastArmyDeploymentTrace.laneId} /{" "}
                {lastArmyDeploymentTrace.armyCardInstanceId} /{" "}
                {lastArmyDeploymentTrace.armyDefinitionId}
              </dd>
            </div>
            <div>
              <dt>Army printed values</dt>
              <dd>
                Cost {formatResources(lastArmyDeploymentTrace.costPaid)} / Upkeep{" "}
                {formatResources(lastArmyDeploymentTrace.printedUpkeep)} / Size{" "}
                {lastArmyDeploymentTrace.armySize} / Attack {lastArmyDeploymentTrace.printedAttack}/
                Guard {lastArmyDeploymentTrace.printedGuard}
              </dd>
            </div>
            <div>
              <dt>Army deployment totals</dt>
              <dd>
                Zone{" "}
                {changed(
                  lastArmyDeploymentTrace.before.incomingZone,
                  lastArmyDeploymentTrace.after.incomingZone,
                )}
                / Count{" "}
                {changed(
                  lastArmyDeploymentTrace.before.armyCount,
                  lastArmyDeploymentTrace.after.armyCount,
                )}
                / Size{" "}
                {changed(
                  lastArmyDeploymentTrace.before.totalArmySize,
                  lastArmyDeploymentTrace.after.totalArmySize,
                )}
                / Attack{" "}
                {changed(
                  lastArmyDeploymentTrace.before.totalAttack,
                  lastArmyDeploymentTrace.after.totalAttack,
                )}
                / Guard{" "}
                {changed(
                  lastArmyDeploymentTrace.before.totalGuard,
                  lastArmyDeploymentTrace.after.totalGuard,
                )}
              </dd>
            </div>
            <div>
              <dt>Army deployment Supply</dt>
              <dd>
                Production {formatResources(lastArmyDeploymentTrace.after.laneProduction)} / Upkeep{" "}
                {formatResources(lastArmyDeploymentTrace.before.totalUpkeep)} -&gt;{" "}
                {formatResources(lastArmyDeploymentTrace.after.totalUpkeep)} / Shortfall{" "}
                {formatResources(lastArmyDeploymentTrace.after.shortfall)} / Sustainable{" "}
                {String(lastArmyDeploymentTrace.projectedNextSupplySustainable)}
              </dd>
            </div>
            <div>
              <dt>Army deployment stability</dt>
              <dd>
                Deployment{" "}
                {changed(
                  lastArmyDeploymentTrace.before.deploymentActionsUsed,
                  lastArmyDeploymentTrace.after.deploymentActionsUsed,
                )}
                / Pool {formatResources(lastArmyDeploymentTrace.before.kingdomPool)} -&gt;{" "}
                {formatResources(lastArmyDeploymentTrace.after.kingdomPool)} / Supply marker{" "}
                {changed(
                  lastArmyDeploymentTrace.before.supplyLifecycleStatus,
                  lastArmyDeploymentTrace.after.supplyLifecycleStatus,
                )}
                / RNG draws{" "}
                {changed(
                  lastArmyDeploymentTrace.before.randomDraws,
                  lastArmyDeploymentTrace.after.randomDraws,
                )}
                / State {lastArmyDeploymentTrace.before.stateHash} -&gt;{" "}
                {lastArmyDeploymentTrace.after.stateHash}
              </dd>
            </div>
          </>
        ) : null}
        <div>
          <dt>State</dt>
          <dd>{game.stateHash}</dd>
        </div>
      </dl>
    </details>
  );
}
